import clsx from "clsx";
import { ArrowLeft, Lock, MessageSquarePlus } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type {
  ActivityEventView,
  CaseDetail,
  CommentView,
  CursorPage,
  PartLineView,
} from "../../../../shared/contracts.js";
import { useMarkCaseRead } from "../../../api/caseMutations.js";
import { fetchCaseDetail } from "../../../api/caseQueries.js";
import { Button, buttonAttributes } from "../../../ui/Button.js";
import { StatusBadge } from "../../../ui/StatusBadge.js";
import {
  etaLabel,
  exceptionLabel,
  remainingQuantity,
} from "../parts-actions/VerifyArrivalDialog.js";
import { technicianLabel, vinEndingLabel } from "../presentation.js";
import { CommentComposer } from "./CommentComposer.js";
import { CommentsPanel } from "./CommentsPanel.js";
import { ActivityEventEntry } from "./ActivityTimeline.js";
import { activityContext, type ActivityContext } from "./activity-sentences.js";
import { formatCalendarDate, formatInstant } from "./PartsCaseInspector.js";

/**
 * The shared workspace's case detail (spec §11 "Case detail"): vehicle and
 * case identity, a large current-status summary with the Arrived countdown,
 * part descriptions and quantities with ETA or No ETA, comments and the
 * activity history — all as content. Order details are controlled by Parts,
 * the page says so, and no editing form (enabled or disabled) exists here.
 * The single primary action is Add comment.
 *
 * Read acknowledgement: each time a case is opened, the exact
 * `readThroughActivityEventId` carried by the response fetched *for that
 * open* is posted back, once. When React Query still holds the case from an
 * earlier open, the cached detail renders first while the open's own fetch
 * is in flight (`isFetching`); the acknowledgement waits for that fetch, so
 * the cursor posted is the one whose events are on screen — never the cached
 * cursor that was already acknowledged the last time. A later background
 * refetch that brings a newer id does not post again — the person has not
 * necessarily seen what arrived — so an event created after the response
 * stays unread until they open the case afresh. A self-authored comment
 * moves the marker server-side inside the comment's transaction; the cache
 * update in `useAddComment` mirrors that.
 *
 * Activity: the server's newest page is shown with a "Show older activity"
 * button while it reports more (spec §13 "may collapse older activity"). A
 * refetch of page one after new events landed pushes its former tail past
 * the page boundary; those displaced events are kept on screen between the
 * fresh page and the older pages, and every event is shown once — nothing
 * loaded is ever silently discarded.
 *
 * The countdown is display only: it counts toward the server's `activeUntil`
 * and never decides whether the case belongs on a list.
 */

export interface SharedCaseDetailProps {
  detail: CaseDetail;
  /** Where "Back to list" goes (the one-pane fallback below 1024 px). */
  backHref: string;
  /**
   * True while the detail query is fetching (or paused offline) — in
   * particular while the fetch that began when this case was opened has not
   * answered and `detail` is still the cached copy from an earlier open. The
   * read acknowledgement waits for it. Omit when `detail` is already the
   * response for this open.
   */
  isFetching?: boolean;
  className?: string;
}

export const CONTROLLED_BY_PARTS_NOTICE =
  "Order details are controlled by Parts. Ask the Parts desk to change parts, quantities, ETAs, assignments or the RO.";

const MINUTE_MS = 60_000;

/** "1 day 12 hr", "5 hr 20 min" or "40 min"; never negative. */
export function formatRemaining(milliseconds: number): string {
  const totalMinutes = Math.max(0, Math.floor(milliseconds / MINUTE_MS));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"} ${hours} hr`;
  if (hours > 0) return `${hours} hr ${minutes} min`;
  return `${minutes} min`;
}

/** Re-renders once a minute while `active` so a countdown stays honest. */
function useMinuteTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), MINUTE_MS);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export function SharedCaseDetail({
  detail,
  backHref,
  isFetching = false,
  className,
}: SharedCaseDetailProps) {
  const { case: record, partLines, comments, permissions } = detail;
  const titleId = useId();
  const partsTitleId = useId();
  const noteTitleId = useId();
  const addCommentRef = useRef<HTMLButtonElement>(null);

  const [composerOpen, setComposerOpen] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);

  // ---- read acknowledgement --------------------------------------------
  const markRead = useMarkCaseRead();
  const markReadMutate = markRead.mutate;
  // The open still waiting for its acknowledgement: set when the case on
  // screen changes, cleared once posted. Declared before the posting effect
  // so that, in one commit, a new open is registered before it is judged.
  const pendingOpenRef = useRef<string | null>(null);
  useEffect(() => {
    pendingOpenRef.current = record.id;
  }, [record.id]);
  const readThroughActivityEventId = detail.readThroughActivityEventId;
  useEffect(() => {
    // The fetch that began with this open has not answered: what is on
    // screen is the cached copy, whose cursor was acknowledged last time.
    if (isFetching) return;
    // Already acknowledged for this open — a background refetch or a
    // self-authored comment that moved the cursor does not post again.
    if (pendingOpenRef.current !== record.id) return;
    pendingOpenRef.current = null;
    if (readThroughActivityEventId) {
      markReadMutate({ caseId: record.id, throughActivityEventId: readThroughActivityEventId });
    }
  }, [record.id, isFetching, readThroughActivityEventId, markReadMutate]);

  // Switching cases drops everything that belonged to the previous one.
  useEffect(() => {
    setComposerOpen(false);
    setAnnouncement("");
    setHighlightCommentId(null);
  }, [record.id]);

  const onCommentAdded = (comment: CommentView) => {
    setHighlightCommentId(comment.id);
    setAnnouncement("Comment added.");
  };

  // ---- status summary ----------------------------------------------------
  const activeUntilMs = record.activeUntil ? new Date(record.activeUntil).getTime() : NaN;
  const showCountdown = record.status === "arrived" && Number.isFinite(activeUntilMs);
  const now = useMinuteTick(showCountdown);
  const remainingMs = showCountdown ? activeUntilMs - now : 0;

  const canComment = permissions.canComment;

  return (
    <article
      className={clsx("shared-case", className)}
      aria-labelledby={titleId}
      data-case-id={record.id}
    >
      <div className="shared-case__toolbar">
        <Link
          to={backHref}
          {...buttonAttributes({
            variant: "quiet",
            size: "touch",
            className: "shared-case__back",
          })}
        >
          <span className="button__icon" aria-hidden="true">
            <ArrowLeft />
          </span>
          <span className="button__label">Back to list</span>
        </Link>
        {canComment ? (
          <Button
            ref={addCommentRef}
            variant="primary"
            icon={<MessageSquarePlus />}
            className="shared-case__add-comment"
            onClick={() => setComposerOpen(true)}
          >
            Add comment
          </Button>
        ) : null}
      </div>

      <header className="shared-case__header">
        <p className="shared-case__number">{record.caseNumber}</p>
        <h2 id={titleId} className="shared-case__title">
          {record.vehicleLabel}
        </h2>
        <p className="shared-case__customer">{record.customerName}</p>
        <dl className="shared-case__facts">
          <div className="shared-case__fact">
            <dt>RO</dt>
            <dd>{record.roNumber ?? "Pre-RO"}</dd>
          </div>
          <div className="shared-case__fact">
            <dt>VIN ending</dt>
            <dd className="shared-case__mono">{vinEndingLabel(record.vinEnding)}</dd>
          </div>
          <div className="shared-case__fact">
            <dt>Advisor</dt>
            <dd>{record.advisorName}</dd>
          </div>
          <div className="shared-case__fact">
            <dt>Technician</dt>
            <dd>{technicianLabel(record.technicianName)}</dd>
          </div>
          {record.appointmentDate ? (
            <div className="shared-case__fact">
              <dt>Appointment</dt>
              <dd>{formatCalendarDate(record.appointmentDate)}</dd>
            </div>
          ) : null}
          <div className="shared-case__fact">
            <dt>Updated</dt>
            <dd>{formatInstant(record.updatedAt)}</dd>
          </div>
        </dl>
      </header>

      <section
        className="status-summary"
        aria-label="Status"
        data-status={record.status}
        data-needs-attention={record.needsAttention ? "true" : undefined}
      >
        <StatusBadge
          status={record.status}
          needsAttention={record.needsAttention}
          size="touch"
          className="status-summary__badge"
        />
        <p className="status-summary__progress">
          <strong>{record.quantityVerified}</strong> of{" "}
          <strong>{record.quantityOrdered}</strong> verified
        </p>
        {record.needsAttention && record.status !== "cancelled" ? (
          <p className="status-summary__note">
            A part line has an exception that blocks Arrived until Parts clears it.
          </p>
        ) : null}
        {showCountdown && record.activeUntil ? (
          <p className="status-summary__countdown">
            {remainingMs > 0 ? (
              <>
                On active lists for <strong>{formatRemaining(remainingMs)}</strong>{" "}
                more — until{" "}
                <time dateTime={record.activeUntil}>
                  {formatInstant(record.activeUntil)}
                </time>
                .
              </>
            ) : (
              <>
                Leaving active lists now — it stays in History and search.
              </>
            )}
          </p>
        ) : null}
      </section>

      <p className="shared-case__notice" role="note">
        <Lock className="shared-case__notice-icon" aria-hidden="true" focusable="false" />
        {CONTROLLED_BY_PARTS_NOTICE}
      </p>

      {/* Polite live region: always present so an announcement is heard. */}
      <p
        role="status"
        aria-live="polite"
        className={clsx(
          "shared-case__announcement",
          !announcement && "shared-case__announcement--empty",
        )}
      >
        {announcement}
      </p>

      {record.orderNote ? (
        <section className="shared-case__section" aria-labelledby={noteTitleId}>
          <h3 id={noteTitleId} className="shared-case__section-title">
            Order note
          </h3>
          <p className="shared-case__note">{record.orderNote}</p>
        </section>
      ) : null}

      <section className="shared-case__section" aria-labelledby={partsTitleId}>
        <h3 id={partsTitleId} className="shared-case__section-title">
          Parts ordered{" "}
          <span className="shared-case__count">({partLines.length})</span>
        </h3>
        <ul className="part-cards">
          {partLines.map((line) => (
            <PartCard key={line.id} line={line} />
          ))}
        </ul>
      </section>

      <CommentsPanel
        comments={comments}
        highlightCommentId={highlightCommentId}
        className="shared-case__section"
      />

      {/* Keyed by case: its pages, displaced events and errors never outlive the case. */}
      <ActivityTimeline
        key={record.id}
        caseId={record.id}
        pageOne={detail.activity}
        context={activityContext(detail)}
        className="shared-case__section"
      />

      <CommentComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        caseId={record.id}
        caseNumber={record.caseNumber}
        onAdded={onCommentAdded}
      />
    </article>
  );
}

/** Every event once, first occurrence wins — the order of `events` is kept. */
export function dedupeActivity(
  events: readonly ActivityEventView[],
): ActivityEventView[] {
  const seen = new Set<string>();
  const unique: ActivityEventView[] = [];
  for (const event of events) {
    if (seen.has(event.id)) continue;
    seen.add(event.id);
    unique.push(event);
  }
  return unique;
}

/**
 * The events a refetched page one no longer carries. Activity is append-only
 * and pages are newest first, so a fresh page one is `[new events…, prefix
 * of the previous page one]` and whatever fell off is its former tail — in
 * order, newer than every older page and older than everything still on
 * page one.
 */
export function displacedFromPageOne(
  previous: readonly ActivityEventView[],
  fresh: readonly ActivityEventView[],
): ActivityEventView[] {
  const onPageOne = new Set(fresh.map((event) => event.id));
  return previous.filter((event) => !onPageOne.has(event.id));
}

interface ActivityTimelineProps {
  caseId: string;
  /** The detail's activity page as the query holds it (newest first). */
  pageOne: CursorPage<ActivityEventView>;
  /** What the sentences may name: the detail's lines and current assignees. */
  context: ActivityContext;
  className?: string;
}

/**
 * The activity history for one case (the parent keys this by case id, so
 * every piece of state below belongs to exactly one case). Shows the server's
 * page one, keeps events that later refetches pushed past its boundary, and
 * appends older pages behind "Show older activity".
 */
function ActivityTimeline({ caseId, pageOne, context, className }: ActivityTimelineProps) {
  const titleId = useId();
  const [olderActivity, setOlderActivity] = useState<readonly ActivityEventView[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null | undefined>(undefined);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState<string | null>(null);

  // Page one as last seen, and the events earlier page ones carried that the
  // current one no longer does (newest first). Reconciled during render, the
  // React pattern for state derived from a changed prop, so the frame that
  // shows a fresh page one already shows its displaced tail too.
  const [seenPageOne, setSeenPageOne] = useState(pageOne.items);
  const [displaced, setDisplaced] = useState<readonly ActivityEventView[]>([]);
  if (seenPageOne !== pageOne.items) {
    const dropped = displacedFromPageOne(seenPageOne, pageOne.items);
    setSeenPageOne(pageOne.items);
    setDisplaced((current) =>
      dedupeActivity([...dropped, ...displacedFromPageOne(current, pageOne.items)]),
    );
  }

  // The first older page follows the page one on screen; later ones follow
  // the last older page loaded. An older page can overlap events displaced
  // from page one in the meantime — the dedupe below shows each once.
  const nextCursor = olderCursor === undefined ? pageOne.nextCursor : olderCursor;
  const items = dedupeActivity([...pageOne.items, ...displaced, ...olderActivity]);

  // A page that arrives after the person moved to another case (this
  // component unmounted) belongs to nobody and is dropped.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadOlderActivity = async () => {
    if (!nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    setOlderError(null);
    try {
      const page = await fetchCaseDetail(caseId, { activityCursor: nextCursor });
      if (!mountedRef.current) return;
      setOlderActivity((current) => [...current, ...page.activity.items]);
      setOlderCursor(page.activity.nextCursor);
    } catch {
      if (!mountedRef.current) return;
      setOlderError("Older activity couldn't be loaded. Try again.");
    } finally {
      if (mountedRef.current) setLoadingOlder(false);
    }
  };

  return (
    <section className={className} aria-labelledby={titleId}>
      <h3 id={titleId} className="shared-case__section-title">
        Activity
      </h3>
      {items.length === 0 ? (
        <p className="shared-case__empty">No activity recorded yet.</p>
      ) : (
        <ol className="activity">
          {items.map((event) => (
            <li key={event.id} className="activity__event">
              <ActivityEventEntry event={event} context={context} classPrefix="activity__" />
            </li>
          ))}
        </ol>
      )}
      {nextCursor ? (
        <div className="shared-case__more">
          <Button
            variant="secondary"
            busy={loadingOlder}
            onClick={() => {
              void loadOlderActivity();
            }}
          >
            Show older activity
          </Button>
          {olderError ? (
            <p role="alert" className="form-alert">
              {olderError}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function PartCard({ line }: { line: PartLineView }) {
  const remaining = remainingQuantity(line);
  return (
    <li
      className="part-card"
      data-complete={remaining === 0 ? "true" : undefined}
      data-exception={line.exception ?? undefined}
    >
      <div className="part-card__identity">
        <p className="part-card__description">{line.description}</p>
        <p className="part-card__number shared-case__mono">{line.partNumber}</p>
      </div>
      <dl className="part-card__facts">
        <div className="part-card__fact">
          <dt>Verified</dt>
          <dd>
            <strong>{line.quantityVerified}</strong> of {line.quantityOrdered}
          </dd>
        </div>
        <div className="part-card__fact">
          <dt>ETA</dt>
          <dd>{etaLabel(line.eta)}</dd>
        </div>
      </dl>
      {line.exception ? (
        <p className="part-card__exception">
          <span className="part-card__exception-label">
            {exceptionLabel(line.exception)}
          </span>
          {line.exceptionReason ? (
            <span className="part-card__exception-reason">{line.exceptionReason}</span>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}
